import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { resetPassword } from '@/api';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

export default function ResetPassword() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => { if (!token) setError(true); }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('ui.passwordMinLength')); return; }
    if (password !== confirmPassword) { toast.error(t('ui.passwordsDontMatch')); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success(t('auth.passwordUpdated'));
    } catch (err) {
      if (err.response?.status === 400) { toast.error(t('auth.invalidToken')); setError(true); }
      else if (err.response?.status === 429) toast.error(t('auth.tooManyRequests'));
      else toast.error(t('common.error'));
    } finally { setLoading(false); }
  };

  const HeaderBar = () => (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-12 sm:h-16 w-auto" /></Link>
          <LanguageSelector />
        </div>
      </div>
    </header>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50">
      <Toaster /><HeaderBar />
      <main className="max-w-lg mx-auto px-4 py-12 sm:py-20">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8 text-red-600" /></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('auth.invalidToken')}</h2>
          <p className="text-slate-600 mb-6">{t('ui.linkExpiredOrInvalid')}</p>
          <Button onClick={() => nav('/forgot-password')} className="bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl h-11 px-8">{t('ui.requestNewLink')}</Button>
        </div>
      </main>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-slate-50">
      <Toaster /><HeaderBar />
      <main className="max-w-lg mx-auto px-4 py-12 sm:py-20">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-green-600" /></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('auth.passwordUpdated')}</h2>
          <p className="text-slate-600 mb-6">{t('ui.nowLoginWithNewPassword')}</p>
          <Button onClick={() => nav('/login')} className="bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl h-11 px-8">{t('auth.login')}</Button>
        </div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster /><HeaderBar />
      <main className="max-w-lg mx-auto px-4 py-12 sm:py-20">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('auth.resetPassword')}</h2>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input name="password" type={showPw ? 'text' : 'password'} placeholder={t('auth.newPassword')} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl pl-10 pr-12" required minLength={6} />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" onClick={() => setShowPw(v => !v)}>{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input name="confirmPassword" type={showConfirmPw ? 'text' : 'password'} placeholder={t('auth.confirmPassword')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="h-11 rounded-xl pl-10 pr-12" required minLength={6} />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" onClick={() => setShowConfirmPw(v => !v)}>{showConfirmPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl">{loading ? t('common.loading') : t('auth.resetPassword')}</Button>
          </form>
        </div>
      </main>
    </div>
  );
}
